// Deutsches Datumsfeld (TT.MM.JJJJ) fuer ISO-Kalendertage — die EINE Anbindung
// von `germanDate.ts` an ein normales Textfeld.
//
// WARUM kein `<input type="date">`: das rendert ZWINGEND im Format der
// UI-Sprache des Browser-PROZESSES und nicht in `navigator.language`. Bei einem
// englischen Chrome steht dort `09/03/2026`. Ausfuehrliche Begruendung und der
// Mess-Gotcha (Playwrights `newContext({locale})` reicht NICHT) stehen am Kopf
// von `germanDate.ts`.
//
// ── VERTRAG (bewusst eng) ───────────────────────────────────────────────────
// `value` und `onCommit` fuehren denselben ISO-Kalendertag „YYYY-MM-DD" bzw.
// "" (= kein Datum), den das native Feld vorher trug. Der Egress bleibt damit
// unveraendert — deutsch werden ausschliesslich Anzeige und Eingabe.
//
// ── WANN COMMITTET WIRD ─────────────────────────────────────────────────────
// Beim VERLASSEN des Feldes (Enter blurrt) UND beim Unmount, jeweils nur mit
// einem vollstaendig lesbaren Datum oder "". Zwei Eigenschaften des nativen
// Feldes werden dabei bewusst nachgebaut, weil an ihnen Aufrufer haengen, die
// SOFORT speichern (TaskDrawer patcht in `onChange`):
//   1. Nur komplette Werte. Per Tastendruck zu committen wuerde den
//      Zwischenstand „1.2" als 01.02. des laufenden Jahres an die API schicken.
//   2. Nur bei echter AENDERUNG. Ein natives Feld feuert `onChange` nicht, wenn
//      man nur hinein- und wieder herausklickt — ein Commit bei jedem Blur
//      wuerde daraus einen PATCH pro Fokuswechsel machen.
// Unlesbarer Text wird NICHT committet: er bleibt stehen, `invalid` traegt die
// sichtbare Meldung, und es geht kein Request raus (kein stiller Fehlwert).
//
// WARUM AUCH BEIM VERSCHWINDEN DES FELDES (Review-Befund D1): Blur allein reicht
// nicht. Wird der umgebende Dialog per ESCAPE oder KLICK DANEBEN geschlossen,
// verschwindet das fokussierte `<input>` aus dem DOM, OHNE dass ein Blur feuert
// — der eingetippte Wert war damit still weg (TaskDrawer schliesst per
// window-keydown, Modal ueber Radix `onOpenChange`). Das sind die zwei
// natuerlichsten Arten, ein Modal zu schliessen. Der Ref-Callback unten holt das
// nach. Dass er nur bei GEAENDERTEM, gueltigem Wert committet, ist doppelt
// wichtig: er laeuft auch im StrictMode-Doppelmount — dort ist der Wert
// unveraendert, also passiert nichts.
// Fuer Formular-Dialoge mit eigenem Speichern-Knopf (Aufgaben-Assistent,
// „Entscheiden") ist das folgenlos: deren `onCommit` setzt nur lokalen State,
// und ein State-Update auf einer verschwindenden Komponente ist ein No-op —
// „Abbrechen verwirft" bleibt also genau so.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent, KeyboardEvent } from "react";
import { fmtDeDate, fmtIsoDate, isoToDeDate, parseDeDate } from "./germanDate";

export interface GermanDateFieldProps {
  /** Meldet, wenn das Feld den DOM verlaesst — der Nachzug-Commit haengt daran.
   *  Beim Spreaden auf `<input>`/`<Input>` (forwardRef) greift React das `ref`
   *  aus den Props heraus wie ein direkt gesetztes. */
  ref: (el: HTMLInputElement | null) => void;
  type: "text";
  value: string;
  placeholder: string;
  inputMode: "numeric";
  "aria-invalid": true | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export interface GermanDateField {
  /** Getippt, aber nicht als Datum lesbar — und das Feld ist verlassen.
   *  Traegt die Fehlermeldung UND sperrt den Speichern-Knopf des Aufrufers. */
  invalid: boolean;
  /** Der ISO-Kalendertag, den der AKTUELL SICHTBARE Text ergibt ("" = leer oder
   *  unlesbar). Fuer Knoepfe, die sofort mit dem Getippten arbeiten sollen,
   *  ohne auf den Commit zu warten — sonst haengt ihr `disabled` an der
   *  Reihenfolge von mousedown/blur/Render statt an einer Zusicherung. */
  pendingIso: string;
  /** In das vorhandene `<input>` spreaden. Eigene Attribute des Aufrufers
   *  (className, aria-label, disabled, title) DANACH setzen, damit sie gewinnen. */
  inputProps: GermanDateFieldProps;
}

/** Standard-Meldung — identisch zu Mail/Kalender, damit dieselbe Fehleingabe
 *  ueberall denselben Satz zeigt. */
export const DE_DATE_HINT = "Bitte als TT.MM.JJJJ angeben (z. B. 01.02.2026).";

export function useGermanDateField(
  value: string,
  onCommit: (iso: string) => void
): GermanDateField {
  const [text, setText] = useState(() => isoToDeDate(value));
  const [focused, setFocused] = useState(false);
  // Spiegel des Fokus als Ref — der stabile Ref-Callback unten kann keinen
  // State lesen, und beim Feld-Austritt zaehlt genau dieser Stand.
  const focusedRef = useRef(false);

  // Der Aufrufer uebergibt fast immer eine frische Arrow-Function; ueber die Ref
  // ist sie beim Commit garantiert die aktuelle, ohne dass er memoisieren muss.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // ZWEI getrennte Merker — sie beantworten zwei verschiedene Fragen, und sie in
  // einen zu legen war ein Fehler:
  //   `seenRef` = letzter Wert, den die Prop hatte. Beantwortet „hat die andere
  //               Seite den Wert geaendert?" und steuert die Anzeige.
  //   `sentRef` = was die andere Seite zuletzt KENNT (von uns gesendet oder von
  //               ihr geliefert). Basis fuer „ist das ueberhaupt eine Aenderung?".
  // Vorher hing beides an EINEM Ref, den der Commit mitverstellte. Weil der
  // Effekt zusaetzlich auf `focused` hoert, lief er direkt nach jedem Blur —
  // sah „Prop != Merker", hielt das fuer eine Fremdaenderung und setzte die
  // Anzeige auf den ALTEN Wert zurueck. Mit echter Antwortzeit sah der Nutzer
  // seine gerade getippte Eingabe sichtbar zurueckschnappen.
  const seenRef = useRef(value);
  const sentRef = useRef(value);
  useEffect(() => {
    // Prop unveraendert → nichts zu tun. Das ist der Fall „unser eigener PATCH
    // fliegt noch"; ihn anzufassen war der Rueckspringer.
    if (value === seenRef.current) return;
    seenRef.current = value;
    sentRef.current = value;
    // Nicht waehrend des Tippens ueberschreiben — sonst springt dem Nutzer das
    // Feld unter den Fingern.
    if (!focused) setText(isoToDeDate(value));
  }, [value, focused]);

  // Aktueller Text fuer den Nachzug-Commit: der Ref-Callback ist stabil und
  // saehe sonst den Stand vom ersten Render.
  const latestText = useRef(text);
  latestText.current = text;

  /** Die EINE Commit-Logik — geteilt von Blur und Feld-Austritt.
   *  `zeigeNormalisiert` ist beim Austritt aus: dort waere ein `setText`
   *  sinnlos (das Feld verschwindet ja gerade). */
  const commit = useCallback((raw: string, zeigeNormalisiert: boolean) => {
    const t = raw.trim();
    // Verglichen wird gegen den ZULETZT AUSGETAUSCHTEN Wert, NICHT gegen die
    // Prop: die zieht erst mit der API-Antwort nach (TaskDrawer.tsx:416-417).
    // Wer gegen die Prop prueft, haelt seinen eigenen, noch fliegenden Commit
    // fuer eine Aenderung und schickt ihn ein zweites Mal — mit echtem Backend
    // ist genau diese Latenz der Normalfall.
    const bekannt = sentRef.current;
    if (t === "") {
      if (zeigeNormalisiert) setText("");
      // Leer ist eine GUELTIGE Angabe („kein Datum"), keine Fehleingabe.
      if (bekannt !== "") {
        sentRef.current = "";
        commitRef.current("");
      }
      return;
    }
    const d = parseDeDate(t, new Date());
    if (!d) return; // unlesbar → stehen lassen, Meldung zeigen, NICHTS senden
    if (zeigeNormalisiert) setText(fmtDeDate(d)); // „1.2." / „12. 3. 26" → „01.02.2026"
    const iso = fmtIsoDate(d);
    if (iso !== bekannt) {
      sentRef.current = iso;
      commitRef.current(iso);
    }
  }, []);

  // Escape / Klick daneben nehmen dem Feld den Fokus NICHT — sie reissen es aus
  // dem DOM. Ohne diesen Nachzug waere der eingetippte Wert still verloren.
  //
  // Ausloeser ist bewusst das ELEMENT, nicht der Unmount der Komponente: ein
  // Cleanup-Effekt reicht nicht, weil `TaskDrawer` gar nicht unmountet — er
  // rendert beim Schliessen nur `null` (TaskDrawer.tsx:539), waehrend die
  // Komponente von Kanban dauerhaft gerendert bleibt (Kanban.tsx:1229). Der
  // Ref-Callback feuert dagegen in BEIDEN Faellen, sobald das `<input>` aus dem
  // DOM genommen wird.
  //
  // Der Callback MUSS stabil sein (`useCallback` mit leeren Deps): bei einer
  // Inline-Arrow wechselt seine Identitaet pro Render, React haengt ihn dann
  // jedes Mal ab und wieder an — und wir wuerden pro Tastendruck committen.
  //
  // Die Fokus-Bedingung unten ist DOPPELSICHERUNG, nicht das tragende Element —
  // gemessen, nicht vermutet: nimmt man sie heraus, bleibt der Walk 56/56 gruen.
  // Was „Erinnerung: Keine" haelt, ist der NACHLAUF von `sentRef`: der Effekt
  // (:108-117) schreibt ihn fort, der Ref-Austritt laeuft in der Commit-Phase
  // davor — zum Austrittszeitpunkt traegt der Merker also noch den alten Wert,
  // und der Wiederauferstehungs-Commit fliegt bei :148 als „keine Aenderung"
  // raus. (Der Vorgaenger-Stand verglich gegen die LEBENDE Prop; dort feuerte er.)
  //
  // Behalten wird sie, weil sie einen Fall abdeckt, den der Merker NICHT sieht:
  // Traegt die Prop einen Wert, den `isoToDeDate` nicht lesen kann (germanDate.ts
  // verlangt exakt ^\d{4}-\d{2}-\d{2}$, z. B. „2026-08-03T00:00:00"), leert der
  // Effekt die Anzeige und setzt den Merker auf den unlesbaren Wert. Text und
  // Merker laufen auseinander, OHNE dass der Nutzer etwas getippt hat — ein
  // Feld-Austritt wuerde dann ein "" committen und ein Datum loeschen, das
  // niemand angefasst hat. Das aktuelle Backend liefert diese Form nicht
  // (date | None auf Date-Spalten → pydantic serialisiert YYYY-MM-DD), die
  // Bedingung sichert also einen Vertragsbruch ab, keinen lebenden Pfad.
  //
  // Der Fokus wird ueber `focusedRef` gefuehrt und NICHT ueber
  // `document.activeElement`: genau der Umstand, der diesen Fehler ausmacht —
  // ein entferntes Feld feuert KEIN Blur — macht `focusedRef` hier zur
  // verlaesslichen Quelle und haengt an keiner Annahme darueber, ob React den
  // Knoten vor oder nach dem Ref-Detach aus dem Dokument nimmt.
  const elRef = useRef<HTMLInputElement | null>(null);
  const setInputEl = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) {
        elRef.current = el;
        return;
      }
      if (!elRef.current) return;
      elRef.current = null;
      // Nur nachziehen, wenn der Nutzer in DIESEM Moment noch im Feld stand.
      // Nach einem normalen Blur ist alles laengst committet.
      if (!focusedRef.current) return;
      focusedRef.current = false;
      commit(latestText.current, false);
    },
    [commit]
  );

  // Waehrend des Tippens ist „12.0" ein Zwischenstand, keine Fehleingabe — die
  // Meldung erscheint deshalb erst nach dem Verlassen des Feldes und
  // verschwindet beim Zurueckkehren sofort wieder, damit sie beim Korrigieren
  // nicht im Weg steht.
  const getippt = text.trim();
  const erkannt = getippt === "" ? null : parseDeDate(getippt, new Date());
  const invalid = !focused && getippt !== "" && erkannt === null;

  return {
    invalid,
    pendingIso: erkannt ? fmtIsoDate(erkannt) : "",
    inputProps: {
      ref: setInputEl,
      type: "text",
      value: text,
      placeholder: "TT.MM.JJJJ",
      inputMode: "numeric",
      "aria-invalid": invalid || undefined,
      onChange: (e) => setText(e.target.value),
      onFocus: () => {
        focusedRef.current = true;
        setFocused(true);
      },
      onBlur: (e) => {
        focusedRef.current = false;
        setFocused(false);
        commit(e.target.value, true);
      },
      onKeyDown: (e) => {
        // Enter uebernimmt (via Blur) — dasselbe Idiom wie beim Titelfeld des
        // TaskDrawers. KEIN neues Kuerzel: Enter im Textfeld ist Standard.
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      },
    },
  };
}
